import _ from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import cn from 'classnames';
import Portal from 'app/components/shared/Portal';
import { bindAll } from '@bibliocommons/utils-react';
import { getHistoryState } from '@bibliocommons/utils-browser';
import ScreenReaderMessage from '@bibliocommons/base-screen-reader-message';
import { Cross } from '@bibliocommons/deprecated-base-icons';
import { childrenShape } from '@bibliocommons/bc-prop-types';

import './FullScreenOverlay.scss';

const ESCAPE_KEY_NAME = 'Escape';
const ESCAPE_KEY_CODE = 27;

export class FullScreenOverlay extends React.PureComponent {
  constructor(props) {
    super(props);

    bindAll(this);

    // Refs seem to have problems inside the Portal so we'll use ref callbacks and set these vars
    this._overlayFocusContent = null;
    this._overlayContent = null;
    this.overlayId = _.uniqueId('overlay');
  }

  componentDidMount() {
    const { open } = this.props;
    if (open) {
      this.handleToggleOpen(open);
    }
  }

  componentDidUpdate(prevProps) {
    if (this.props.open !== prevProps.open) {
      this.handleToggleOpen(this.props.open);
    }
  }

  componentWillUnmount() {
    if (this.props.open) {
      this.handleToggleOpen(false);
    }
  }

  // Static property to track open overlays
  static openOverlays = [];

  handleToggleOpen(open) {
    // Add or remove event listeners based on the open state
    const eventListener = open ? 'addEventListener' : 'removeEventListener';
    document[eventListener]('click', this.handleDocumentClick, true);
    document[eventListener]('keydown', this.handleCloseOnEscape, true);

    if (open) {
      FullScreenOverlay.openOverlays.push(this);
    } else {
      const index = FullScreenOverlay.openOverlays.indexOf(this);
      if (index !== -1) {
        FullScreenOverlay.openOverlays.splice(index, 1);
      }
    }

    const toggle = open ? 'add' : 'remove';
    document.body.classList[toggle]('overlay-is-open');

    // Allow the overlay to be closed by the browser back button
    if (!this.props.disableClose) {
      window[eventListener]('popstate', this.handlePopState);
    }

    if (open) {
      if (!this.props.disableClose && !this.props.disablePushState) {
        window.history.pushState({ ...getHistoryState(), overlayId: this.overlayId }, '');
      }
      if (this._overlayContent) this._overlayContent.scrollTop = 0;
    }
  }

  isActiveOverlay() {
    const openOverlays = FullScreenOverlay.openOverlays;
    return openOverlays.length > 0 && openOverlays[openOverlays.length - 1] === this;
  }

  handleDocumentClick(e) {
    // If clicking outside the overlay's content, call the close handler
    if (this.props.open && this.isActiveOverlay()) {
      const contentIsTarget = this._overlayFocusContent && this._overlayFocusContent.contains(e.target);

      if (document.body.contains(e.target) && !contentIsTarget) {
        this.handleClose();
      }
    }
  }

  handleCloseOnEscape(e) {
    if (e.key === ESCAPE_KEY_NAME || e.which === ESCAPE_KEY_CODE) {
      if (this.isActiveOverlay()) {
        this.handleClose();
      }
    }
  }

  handleClose() {
    if (!this.props.disableClose) {
      if (this.props.disablePushState) {
        this.props.handleClose();
      } else {
        this.props.handleClose();
        window.history.back();
      }
    }
  }

  handlePopState() {
    if (this.props.open && !this.props.disableClose) {
      this.props.handleClose();
    }
  }

  render() {
    const classes = cn('cp-deprecated-full-screen-overlay', this.props.animation, this.props.className, {
      open: this.props.open
    });

    return (
      <Portal focusTrapActive={this.props.open}>
        <div className={classes} role="dialog" aria-hidden={!this.props.open} data-test-id={this.props['data-test-id']}>
          <div className="overlay-backdrop" />
          <div
            className="overlay-inner"
            ref={el => {
              this._overlayFocusContent = el;
            }}
          >
            <div
              className="overlay-body"
              ref={el => {
                this._overlayContent = el;
              }}
            >
              {this.props.children}
            </div>
            {!this.props.disableClose && (
              <button
                className="overlay-close"
                data-key="overlay-close-button"
                type="button"
                onClick={this.handleClose}
              >
                <span aria-hidden>
                  <Cross />
                </span>
                <ScreenReaderMessage>
                  <FormattedMessage id="close_overlay" />
                </ScreenReaderMessage>
              </button>
            )}
          </div>
        </div>
      </Portal>
    );
  }
}

FullScreenOverlay.propTypes = {
  animation: PropTypes.oneOf(['simple-genie', 'slide-down', 'fade-in', 'instant']),
  open: PropTypes.bool,
  handleClose: PropTypes.func,
  disableClose: PropTypes.bool,
  disablePushState: PropTypes.bool,
  className: PropTypes.string,
  children: childrenShape,
  'data-test-id': PropTypes.string
};

FullScreenOverlay.defaultProps = {
  animation: 'simple-genie',
  handleClose: _.noop,
  disableClose: false
};

export default FullScreenOverlay;
